// What HMRC's fraud prevention headers need from the browser, sent to the API
// in the Elysian-Client-Info header (base64 of JSON) on Making Tax Digital
// calls. The API adds what it sees itself (public IP and port, the user, a
// second sign-in factor) and builds the Gov-Client-* headers.

import { randomUuid } from '@elysian/api-client';

const DEVICE_KEY = 'elysian.device-id';

/** A random id for this browser, kept so HMRC sees the same device each time. */
export function deviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_KEY);
    if (existing && /^[0-9a-f-]{36}$/i.test(existing)) return existing;
    const id = randomUuid();
    localStorage.setItem(DEVICE_KEY, id);
    return id;
  } catch {
    return randomUuid();
  }
}

export interface ClientInfo {
  device_id: string;
  user_agent: string;
  screens: { width: number; height: number; scaling_factor: number; colour_depth: number }[];
  window_width: number;
  window_height: number;
  /** Minutes east of UTC (JavaScript's getTimezoneOffset has the opposite sign). */
  utc_offset_minutes: number;
}

export function clientInfo(): ClientInfo {
  const s = window.screen;
  return {
    device_id: deviceId(),
    user_agent: navigator.userAgent,
    screens: s ? [{ width: s.width, height: s.height, scaling_factor: window.devicePixelRatio || 1, colour_depth: s.colorDepth }] : [],
    window_width: window.innerWidth,
    window_height: window.innerHeight,
    utc_offset_minutes: 0 - new Date().getTimezoneOffset() || 0, // never -0: HMRC compares the text
  };
}

/** The header value: base64 of UTF-8 JSON. */
export function clientInfoHeader(info: ClientInfo = clientInfo()): string {
  const bytes = new TextEncoder().encode(JSON.stringify(info));
  let binary = '';
  bytes.forEach((b) => { binary += String.fromCharCode(b); });
  return btoa(binary);
}
